
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {DataFeedDeleteResponse} from './DataFeedDeleteResponse';

/**
 * DataFeedDeleteRequest struct
 */
class DataFeedDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} dataFeedId
   * @param {string=} dataFeedName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), dataFeedId = null, dataFeedName = null) {
    super()
    super.copy(parent)
    this.DataFeedId = dataFeedId
    this.DataFeedName = dataFeedName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DataFeedDeleteRequest}
   * @param {!DataFeedDeleteRequest} other Other struct
   * @returns {!DataFeedDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.DataFeedId != null) {
      this.DataFeedId = new UUID(other.DataFeedId)
    } else {
      this.DataFeedId = null
    }
    if (other.DataFeedName != null) {
      this.DataFeedName = other.DataFeedName
    } else {
      this.DataFeedName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DataFeedDeleteRequest}
   * @returns {!DataFeedDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DataFeedDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DataFeedDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DataFeedDeleteRequest}
   * @param {!DataFeedDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DataFeedDeleteRequest)) {
      throw new TypeError('Instance of DataFeedDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DataFeedDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      DataFeedId: ((this.DataFeedId != null) ? this.DataFeedId.toString() : null),
      DataFeedName: ((this.DataFeedName != null) ? this.DataFeedName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DataFeedDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DataFeedDeleteRequest} other Object value
   * @returns {!DataFeedDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new DataFeedDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DataFeedDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DataFeedDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DataFeedDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6339
  }
}

export { DataFeedDeleteRequest };

/**
 * Fast Binary Encoding DataFeedDeleteRequest field model
 */
class FieldModelDataFeedDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.DataFeedId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.DataFeedName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.DataFeedId.fbeOffset + this.DataFeedId.fbeSize), buffer, this.DataFeedId.fbeOffset + this.DataFeedId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.DataFeedId.fbeSize + this.DataFeedName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.DataFeedId.fbeExtra + this.DataFeedName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDataFeedDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6339
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DataFeedId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DataFeedId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DataFeedId.fbeSize

    if ((fbeCurrentSize + this.DataFeedName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DataFeedName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DataFeedName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {!DataFeedDeleteRequest} fbeValue Default value, defaults is new DataFeedDeleteRequest()
   * @returns {!DataFeedDeleteRequest} DataFeedDeleteRequest value
   */
  get (fbeValue = new DataFeedDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {!DataFeedDeleteRequest} fbeValue DataFeedDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.DataFeedId.fbeSize) <= fbeStructSize) {
      fbeValue.DataFeedId = this.DataFeedId.get()
    } else {
      fbeValue.DataFeedId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DataFeedId.fbeSize

    if ((fbeCurrentSize + this.DataFeedName.fbeSize) <= fbeStructSize) {
      fbeValue.DataFeedName = this.DataFeedName.get()
    } else {
      fbeValue.DataFeedName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DataFeedName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDataFeedDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {!DataFeedDeleteRequest} fbeValue DataFeedDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDataFeedDeleteRequest}
   * @param {DataFeedDeleteRequest} fbeValue DataFeedDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.DataFeedId.set(fbeValue.DataFeedId)
    this.DataFeedName.set(fbeValue.DataFeedName)
  }
}

export { FieldModelDataFeedDeleteRequest };

/**
 * Fast Binary Encoding DataFeedDeleteRequest model
 */
class DataFeedDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDataFeedDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the DataFeedDeleteRequest model
   * @this {!DataFeedDeleteRequestModel}
   * @returns {!FieldModelDataFeedDeleteRequest} model DataFeedDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DataFeedDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DataFeedDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DataFeedDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DataFeedDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDataFeedDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DataFeedDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DataFeedDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DataFeedDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DataFeedDeleteRequestModel}
   * @param {!DataFeedDeleteRequest} value DataFeedDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DataFeedDeleteRequestModel}
   * @param {!DataFeedDeleteRequest} value DataFeedDeleteRequest value, defaults is new DataFeedDeleteRequest()
   * @return {!object} Deserialized DataFeedDeleteRequest value and its size
   */
  deserialize (value = new DataFeedDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DataFeedDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DataFeedDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DataFeedDeleteRequestModel}
   * @param {!number} prev Previous DataFeedDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DataFeedDeleteRequestModel };
DataFeedDeleteRequest.__has_response = true;
DataFeedDeleteRequest.__response_class = DataFeedDeleteResponse;
